import { Controller } from "@hotwired/stimulus";

/**
 * Cloudflare Turnstile widget for forms protected by TurnstileVerifiable (rendered by the
 * turnstile_widget helper). The widget is rendered explicitly on connect: Turnstile's implicit
 * mode only scans the page once, when api.js first loads, so widgets that arrive through a Turbo
 * visit or a Turbo Stream re-render stayed empty and every submission failed verification.
 *
 * Turnstile adds a hidden cf-turnstile-response input inside the element, which the form submits.
 */

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileOptions {
  sitekey: string;
  theme: string;
  size: string;
  appearance: string;
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileOptions): string | undefined;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile script loaded without window.turnstile"));
    };
    script.onerror = () => {
      scriptPromise = null; // let the next connect() try again
      script.remove();
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export default class extends Controller<HTMLElement> {
  static values = {
    sitekey: String,
    theme: { type: String, default: "light" },
    size: { type: String, default: "normal" },
    appearance: { type: String, default: "interaction-only" },
  };

  declare sitekeyValue: string;
  declare themeValue: string;
  declare sizeValue: string;
  declare appearanceValue: string;

  private widgetId: string | null = null;

  async connect() {
    let turnstile: TurnstileApi;
    try {
      turnstile = await loadTurnstile();
    } catch (error) {
      // Blocked by an extension or offline - the server rejects the form with a clear message
      console.warn(error);
      return;
    }

    // The element may have left the page while the script was loading
    if (!this.element.isConnected || this.widgetId) return;

    this.widgetId =
      turnstile.render(this.element, {
        sitekey: this.sitekeyValue,
        theme: this.themeValue,
        size: this.sizeValue,
        appearance: this.appearanceValue,
      }) ?? null;
  }

  disconnect() {
    if (this.widgetId && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
    this.widgetId = null;
  }
}
